import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card, CardContent, Typography, Button, Divider,
  List, ListItem, Chip, Box
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';

const FEATURE_ORDER = [
  ['pedidos', 'Pedidos'],
  ['usuariosWeb', 'Usuarios Web'],
  ['usuariosMobile', 'App Conductor'],
  ['entregas', 'Entregas'],
  ['rutas', 'Rutas'],
  ['optimizacion', 'Optimización'],
  ['erp', 'ERP (Según Disponibilidad)'],
  ['mercadoLibre', 'Integración Mercado Libre'],
  ['marketplace', 'Integración MarketPlace'],
  ['pickeo', 'Pickeo'],
];

function formatUF(n) {
  const f = new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return f.format(Number(n));
}

export default function SubscriptionCard({ plan, highlight = false, onSelect }) {
  const isHighlighted = highlight || plan?.recommended;

  const {
    discountedYearly,
    originalYearly,
    hasDiscount,
    ctaText,
  } = useMemo(() => {
    const isPaid = plan?.type === 'paid';
    const applyDiscount = (val) =>
      plan?.discount ? Number(val * (1 - plan.discount)) : val;

    const yearly = isPaid && plan.yearlyPrice ? `${formatUF(plan.yearlyPrice)} UF/mes` : null;
    const dYearlyVal  = isPaid && plan.yearlyPrice ? applyDiscount(plan.yearlyPrice) : null;
    const dYearly  = dYearlyVal  != null ? `${formatUF(dYearlyVal)} UF/mes` : null;

    let cta = 'Elegir plan';
    if (plan?.type === 'demo') cta = 'Agendar demo';
    if (plan?.type === 'evaluation') cta = 'Solicitar evaluación';

    return {
      originalYearly: yearly,
      discountedYearly: dYearly,
      hasDiscount: Boolean(dYearly),
      ctaText: cta,
    };
  }, [plan]);

  // Construye filas en orden, fusionando ML + Marketplace en PRO
  const rows = useMemo(() => {
    const items = [];
    for (const [key, label] of FEATURE_ORDER) {
      if (plan?.slug === 'pro' && key === 'mercadoLibre') {
        items.push({
          label: 'Integraciones (ML & Marketplace)',
          value: plan.features.mercadoLibre,
          key: 'integraciones',
        });
        continue;
      }
      if (plan?.slug === 'pro' && key === 'marketplace') {
        continue; // omitida porque se fusionó
      }
      items.push({ label, value: plan.features[key], key });
    }
    return items;
  }, [plan]);

  // Render de valor (ícono ✅, ❌ o texto)
  const renderValue = (value) => {
    if (value === '✅') return <CheckCircleRoundedIcon fontSize="small" />;
    if (value === '—' || value === '❌') return <DoNotDisturbAltRoundedIcon fontSize="small" />;
    return (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    );
  };

  return (
    <Card
      variant={isHighlighted ? 'elevation' : 'outlined'}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        borderColor: 'divider',
        boxShadow: isHighlighted ? 8 : 2,
        transition: 'transform .25s ease, box-shadow .25s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 8 },
      }}
      role="region"
      aria-label={`Plan ${plan?.name}`}
    >
      {/* Franja de color de plan */}
      <Box
        sx={{
          height: 10,
          background: plan?.color || 'primary.main',
        }}
      />

      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: 0.2 }}>
            {plan?.name}
          </Typography>
          {isHighlighted && (
            <Chip
              label="Recomendado"
              color="primary"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Box>

        {/* Precios / Mensaje */}
        <Box mb={2} mt={2} textAlign="center" sx={{ position: 'relative', display: 'inline-block' }}>
          {plan.type === 'demo' && (
            <Typography variant="body1" fontWeight={600} mb={1}>
              {plan.description}
            </Typography>
          )}
          {plan.type === 'evaluation' && (
            <Typography variant="body1" fontWeight={600} mb={4}>
              {plan.description}
            </Typography>
          )}
          {plan.type === 'paid' && hasDiscount && (
            <>
              {/* Precio con descuento */}
              <Typography
                variant="h3"
                fontWeight={900}
                sx={{ color: plan.color || 'primary.main' }}
              >
                {discountedYearly}
                {plan.slug === 'pyme' && (
                  <Typography component="span" variant="body2" sx={{ ml: 1 }} >
                    por usuario
                  </Typography>
                )}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                (más IVA)
              </Typography>

              {/* Precio original tachado en diagonal */}
              {originalYearly && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -10,
                    textDecoration: 'line-through',
                    transform: 'skewY(-5deg)',
                    opacity: 0.7,
                  }}
                >
                  {originalYearly}
                </Typography>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Lista de features */}
        <List
          dense
          disablePadding
          sx={{
            mt: 1,
            '& .feature-row': {
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              columnGap: 2,
              minHeight: 40,   // 👈 misma altura en todas las filas
              py: 0.5,
            },
          }}
        >
          {rows.map(({ key, label, value }) => (
            <ListItem key={key} className="feature-row" disableGutters>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {renderValue(value)}
              </Box>
            </ListItem>
          ))}
        </List>

        {/* CTA */}
        <Box mt="auto">
          <Button
            fullWidth
            variant={isHighlighted ? 'contained' : 'outlined'}
            size="medium"
            sx={{ mt: 2, fontWeight: 700 }}
            onClick={() => onSelect?.(plan)}
          >
            {ctaText}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

SubscriptionCard.propTypes = {
  plan: PropTypes.object.isRequired,
  highlight: PropTypes.bool,
  onSelect: PropTypes.func,
};
